/**
 * The date range picker: a button that names the range in use and opens ready-made ranges beside
 * two months of calendar. Click a day to start, another to end; the range goes into the URL
 * (lib/range.ts), so every table and number on the page follows it. Days are the Google Ads
 * account's own days, and days that have not come yet cannot be picked.
 */
import { addDays, daysBetween, todayIn } from "@brecx/shared";
import { useEffect, useRef, useState } from "react";
import { addMonths, monthLabel, monthOf, monthWeeks, presets, rangeLabel } from "../lib/calendar";
import { MAX_RANGE_DAYS, useRange } from "../lib/range";
import { Icon } from "./Icon";
import "./DateRange.css";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
/** About how wide the popup is, to keep it on the screen. */
const POP_W = 700;

export function DateRange({
  timeZone,
  compact,
}: {
  /** the Google Ads account's time zone: the clock "today" is on */
  timeZone?: string;
  /** beside the 7d–90d buttons: a small button that only names dates picked by hand */
  compact?: boolean;
}) {
  const range = useRange();
  const [open, setOpen] = useState(false);
  // the first day clicked, waiting for the second
  const [start, setStart] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const today = todayIn(timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  // the month on the left; the one after it is on the right
  const [left, setLeft] = useState(() => addMonths(monthOf(today), -1));
  const box = useRef<HTMLDivElement>(null);
  // where the popup hangs: placed on the screen itself, so a bar that hides its overflow cannot cut it
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);

  // what is in use now, as dates: the "last N days" ranges end yesterday
  const used = range.custom ?? { from: addDays(today, -range.days), to: addDays(today, -1) };

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // the popup does not follow the page: scrolling or resizing closes it
    const gone = (e: Event) => {
      if (!(e.target instanceof Node && box.current?.contains(e.target))) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    window.addEventListener("resize", gone);
    window.addEventListener("scroll", gone, true);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("resize", gone);
      window.removeEventListener("scroll", gone, true);
    };
  }, [open]);

  const toggle = () => {
    if (!open) {
      setStart(null);
      setHover(null);
      // open on the months that hold the end of the range in use
      setLeft(addMonths(monthOf(used.to), -1));
      const r = box.current?.getBoundingClientRect();
      // under the button, its right edge on the button's; never past the left of the screen
      if (r)
        setAt({
          top: r.bottom + 6,
          right: Math.max(8, Math.min(window.innerWidth - r.right, window.innerWidth - POP_W - 8)),
        });
    }
    setOpen(!open);
  };

  // while the second day is being chosen, the range follows the pointer
  const end = start ? (hover ?? start) : null;
  const shown = start && end ? { from: start < end ? start : end, to: start < end ? end : start } : used;
  const tooLong = daysBetween(shown.from, shown.to) + 1 > MAX_RANGE_DAYS;

  const pick = (day: string) => {
    if (!start) {
      setStart(day);
      setHover(null);
      return;
    }
    const from = start < day ? start : day;
    const to = start < day ? day : start;
    if (daysBetween(from, to) + 1 > MAX_RANGE_DAYS) return;
    range.setCustom(from, to);
    setOpen(false);
  };

  const month = (m: string) => (
    <div className="dr-month">
      <table className="dr-grid" aria-label={monthLabel(m)}>
        <thead>
          <tr>
            {WEEKDAYS.map((d) => (
              <th key={d} scope="col">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthWeeks(m).map((week) => (
            <tr key={week[0]}>
              {week.map((day) => {
                const inRange = day >= shown.from && day <= shown.to;
                const edge = day === shown.from || day === shown.to;
                const cls = [
                  monthOf(day) === m ? "" : "out",
                  inRange ? "in" : "",
                  edge ? "edge" : "",
                  day === today ? "today" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <td key={day}>
                    <button
                      type="button"
                      className={cls}
                      disabled={day > today}
                      aria-label={rangeLabel(day, day)}
                      aria-pressed={edge}
                      onClick={() => pick(day)}
                      onPointerEnter={() => start && day <= today && setHover(day)}
                      onFocus={() => start && setHover(day)}
                    >
                      {Number(day.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const right = addMonths(left, 1);
  return (
    <div className="dr" ref={box}>
      <button
        type="button"
        className={`btn${compact ? " sm" : ""}${range.custom ? " dr-on" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Choose the dates the numbers cover"
        onClick={toggle}
      >
        <Icon name="calendar" />
        {range.custom
          ? rangeLabel(range.custom.from, range.custom.to)
          : compact
            ? "Custom"
            : `Last ${range.days} days`}
      </button>
      {open ? (
        <div className="dr-pop" role="dialog" aria-label="Date range" style={at ?? undefined}>
          <ul className="dr-presets">
            {presets(today).map((p) => {
              const on = p.days
                ? !range.custom && range.days === p.days
                : range.custom?.from === p.from && range.custom?.to === p.to;
              return (
                <li key={p.label}>
                  <button
                    type="button"
                    className={on ? "on" : ""}
                    aria-pressed={on}
                    onClick={() => {
                      if (p.days) range.setDays(p.days);
                      else if (p.from && p.to) range.setCustom(p.from, p.to);
                      setOpen(false);
                    }}
                  >
                    {p.label}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="dr-cal">
            <div className="dr-nav">
              <button type="button" aria-label="A year back" onClick={() => setLeft(addMonths(left, -12))}>
                «
              </button>
              <button type="button" aria-label="A month back" onClick={() => setLeft(addMonths(left, -1))}>
                ‹
              </button>
              <b>{monthLabel(left)}</b>
              <b>{monthLabel(right)}</b>
              <button
                type="button"
                aria-label="A month on"
                disabled={right >= monthOf(today)}
                onClick={() => setLeft(addMonths(left, 1))}
              >
                ›
              </button>
              <button
                type="button"
                aria-label="A year on"
                disabled={addMonths(right, 12) > monthOf(today)}
                onClick={() => setLeft(addMonths(left, 12))}
              >
                »
              </button>
            </div>
            <div className="dr-months">
              {month(left)}
              {month(right)}
            </div>
            <div className="dr-foot" aria-live="polite">
              {start ? (
                <>
                  <b>{rangeLabel(shown.from, shown.to)}</b>
                  <span className="muted">
                    {tooLong
                      ? `A range can be ${MAX_RANGE_DAYS} days at most. Pick a closer day.`
                      : "Click another day to end the range."}
                  </span>
                </>
              ) : (
                <>
                  <b>{rangeLabel(used.from, used.to)}</b>
                  <span className="muted">Click a day to start, then click another to end.</span>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
